import { api } from './client';

export type InvoiceStatus = 'DRAFT' | 'ISSUED' | 'PARTIALLY_PAID' | 'PAID' | 'CANCELLED';

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Черновик',
  ISSUED: 'Выставлен',
  PARTIALLY_PAID: 'Оплачен частично',
  PAID: 'Оплачен',
  CANCELLED: 'Отменён',
};

export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  DRAFT: 'gray',
  ISSUED: 'blue',
  PARTIALLY_PAID: 'yellow',
  PAID: 'green',
  CANCELLED: 'red',
};

export interface InvoiceLine {
  id: string;
  lineNumber: number;
  nomenclatureId: string | null;
  name: string;
  article: string | null;
  unitId: string | null;
  unitCode: string | null;
  unitSymbol: string | null;
  quantity: string;
  price: string;
  vatRateCode: string;
  vatRateValue: string | null;
  amount: string;
  vatAmount: string;
  totalAmount: string;
}

export interface InvoiceLineInput {
  nomenclatureId?: string | null;
  name: string;
  article?: string | null;
  unitId: string;
  quantity: string;
  price: string;
  vatRateId: string;
}

export interface InvoiceOrganizationRef {
  id: string;
  shortName: string;
  fullName: string;
  kind: 'LEGAL_ENTITY' | 'ENTREPRENEUR' | 'INDIVIDUAL' | 'FOREIGN';
  inn: string;
  kpp: string | null;
  ogrn: string | null;
  addressFull: string | null;
  phone: string | null;
  email: string | null;
  directorPosition: string | null;
  directorName: string | null;
  accountantName: string | null;
  accountantIsDirector: boolean;
}

export interface InvoiceCounterpartyRef {
  id: string;
  shortName: string;
  fullName: string;
  inn: string | null;
  kpp: string | null;
  addressFull: string | null;
  phone: string | null;
}

export interface InvoiceBankAccountRef {
  id: string;
  accountNumber: string;
  bankName: string;
  bik: string;
  corrAccount: string;
}

export interface Invoice {
  id: string;
  organizationId: string;
  organization: InvoiceOrganizationRef;
  counterpartyId: string;
  counterparty: InvoiceCounterpartyRef;
  contractId: string | null;
  number: string;
  numberInt: number;
  date: string;
  dueDate: string | null;
  bankAccountId: string | null;
  bankAccount: InvoiceBankAccountRef | null;
  status: InvoiceStatus;
  subtotal: string;
  vatAmount: string;
  total: string;
  paidAmount: string;
  paidAt: string | null;
  comment: string | null;
  footerText: string | null;
  lines: InvoiceLine[];
  upds: Array<{ id: string; number: string; date: string }>;
  acts: Array<{ id: string; number: string; date: string }>;
}

export interface InvoiceInput {
  organizationId: string;
  counterpartyId: string;
  contractId?: string | null;
  date: string;
  dueDate?: string | null;
  bankAccountId?: string | null;
  comment?: string | null;
  footerText?: string | null;
  lines: InvoiceLineInput[];
}

export interface InvoiceListResponse {
  items: Invoice[];
  total: number;
  page: number;
  pageSize: number;
}

export interface InvoiceListParams {
  search?: string;
  organizationId?: string;
  counterpartyId?: string;
  status?: InvoiceStatus;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: InvoiceListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.organizationId) search.set('organizationId', params.organizationId);
  if (params.counterpartyId) search.set('counterpartyId', params.counterpartyId);
  if (params.status) search.set('status', params.status);
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const invoicesApi = {
  list: (params: InvoiceListParams = {}) => api.get<InvoiceListResponse>(`/invoices${buildQuery(params)}`),

  get: (id: string) => api.get<{ invoice: Invoice }>(`/invoices/${id}`),

  create: (data: InvoiceInput) => api.post<{ invoice: Invoice }>('/invoices', data),

  update: (id: string, data: InvoiceInput) => api.patch<{ invoice: Invoice }>(`/invoices/${id}`, data),

  remove: (id: string) => api.delete<void>(`/invoices/${id}`),

  issue: (id: string) => api.post<{ invoice: Invoice }>(`/invoices/${id}/issue`),

  cancel: (id: string) => api.post<{ invoice: Invoice }>(`/invoices/${id}/cancel`),

  restore: (id: string) => api.post<{ invoice: Invoice }>(`/invoices/${id}/restore`),

  registerPayment: (id: string, data: { paidAmount: string; paidAt?: string }) =>
    api.post<{ invoice: Invoice }>(`/invoices/${id}/payment`, data),
};
